import React from "react";
import { Helmet } from "react-helmet-async";
import {
  SITE_PERSON_SCHEMA,
  SITE_ORGANIZATION_SCHEMA,
  SITE_WEBSITE_SCHEMA,
} from "@/lib/siteSchema";

export interface SEOHeadProps {
  title: string;
  description: string;
  canonical: string; // absolute URL, e.g. https://iamieshiadanielle.com/about
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: Record<string, unknown>[]; // page-specific @graph entries
}

const DEFAULT_OG_IMAGE =
  "https://vibe.filesafe.space/1787853701390843161/assets/a5d05b54-0df1-474c-a727-ad33093a5f8e.png";

const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  schemaJson = [],
}) => {
  const graph = [
    SITE_PERSON_SCHEMA,
    SITE_ORGANIZATION_SCHEMA,
    SITE_WEBSITE_SCHEMA,
    ...schemaJson,
  ];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noIndex ? "noindex, nofollow" : "index, follow"}
      />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({ "@context": "https://schema.org", "@graph": graph })}
      </script>
    </Helmet>
  );
};

export default SEOHead;
